import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Check } from 'lucide-react';
import { RECRUIT_STAGES, readRecruitStage, writeRecruitStage } from '../recruitStage';
import { useCanEditClientNotes } from '../clientNotesStore';
import RecruitStageDot from './RecruitStageDot';
import type { IntlLike } from '../clientPresentation';

/**
 * The list's 階段 menu — one for the whole page, anchored to whichever row pill
 * opened it. Two hundred rows each owning a closed MUI Menu would be two
 * hundred portals nobody opened.
 *
 * Deliberately the SAME menu the drawer's header pill shows: same six options,
 * same dots, same tick, and the same 「pick the current one again to clear it」.
 * The adviser meets this control in two places and it behaves once.
 */
export type RecruitStageMenuProps = {
  anchor: HTMLElement | null;
  clientId: string | null;
  onClose: () => void;
  intl?: IntlLike;
};

const RecruitStageMenu = ({ anchor, clientId, onClose, intl }: RecruitStageMenuProps) => {
  // Read at open time rather than subscribing: the menu only exists while it is
  // open, and the write below closes it.
  const current = anchor && clientId ? readRecruitStage(clientId) : null;
  const label = (id: string) => intl?.formatMessage({ id }) ?? id;
  // No factsheet, nowhere to save a stage: the six options stay readable (the
  // current one ticked) but cannot be picked, and the menu says why.
  const canSave = useCanEditClientNotes(clientId);

  return (
    <Menu anchorEl={anchor} open={Boolean(anchor && clientId)} onClose={onClose} data-testid="row-stage-menu">
      {clientId && !canSave && (
        <li className="clientv2-recruit-stage-readonly" data-testid="row-stage-readonly" role="presentation">
          {label('clientv2-notes-no-factsheet')}
        </li>
      )}
      {RECRUIT_STAGES.map(option => (
        <MenuItem
          key={option}
          selected={option === current}
          disabled={!canSave}
          data-testid="row-stage-option"
          data-stage={option}
          onClick={() => {
            // Picking the current stage again clears it, exactly as the
            // drawer's menu does.
            writeRecruitStage(clientId, option === current ? null : option);
            onClose();
          }}
        >
          <RecruitStageDot stage={option} className="clientv2-recruit-stage-dot" />
          {label(`clientv2-recruit-stage-${option}`)}
          {option === current && <Check size={14} className="clientv2-recruit-stage-check" aria-hidden="true" />}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default RecruitStageMenu;
